import React, { PureComponent } from 'react';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { Translate, withLocalize } from 'react-localize-redux';
import dayjs from 'dayjs';
import { compose } from 'redux';
import styled from 'styled-components';

import Gallery from '../../components/gallery';
import Indicator from '../../components/indicator';
import Link from '../../components/link';
import Modal from '../../components/modal';
import { MINIMUM_PERCENTAGE_COMPLETE_TO_SUCCESS } from '../../constants';
import firebase from '../../firebase';
import logos from '../../logos/games';
import locale from './locale';

const ModalContainer = styled.div`
  font-size: 1.5em;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
`;

const ModalText = styled.div`
  text-align: center;
  font-size: 0.8em;
  color: #111111;
`;

const Item = styled.div`
  margin: 0 auto;
  cursor: pointer;
  padding: 1em;
  width: 12em;
  display: flex;
  flex-direction: column;
  height: 12em;
  align-items: center;
`;

const BookingContainer = styled.section`
  margin: 0 2em 2em;
`;

const Ribbon = styled.div`
  background: #535353;
  padding: 0 1em;
  height: 4em;
  border-radius: 2em;
  z-index: 1;
  position: relative;
  align-items: center;
  display: flex;
`;

const Header = styled.header`
  flex: 1;
  min-width: 0;
  > * {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1em;
  line-height: 1.6em;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.8em;
  line-height: 1.5em;
  color: #b8b8b8;
`;

const ModalIcon = styled.img`
  height: 80%;
  max-width: 100%;
`;

const Caption = styled.div`
  height: 20%;
  padding: 1em 0 0;
`;

const BookingMetrics = styled.div`
  text-align: center;
`;

const BookingContent = styled.div`
  background: #3c3c3c;
  margin-top: -2em;
  padding: 3em 0 1.5em 0;
  border-radius: 0 0 1em 1em;
`;

const BookingButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  height: 100%;
  font-size: 1.4em;
  width: 2em;
  outline: none;
`;
const Status = styled.div`
  margin: 0 2em 2em 2em;
  text-align: center;
`;
const ScoreLabel = styled.div`
  font-weight: bold;
  color: ${props => props.theme.primary};
`;

const ScorePercentage = styled.div`
  font-size: 4.5em;
  font-family: DINCondensed-Regular;
  letter-spacing: 0.2rem;
  margin: 0.2em 0 0;
  line-height: 0.8em;
`;
const ScoreStatus = styled.div`
  font-size: 1.5em;
  font-weight: bold;
`;

const ListItem = styled.div`
  height: 3em;
  padding: 0 2em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:nth-child(odd) {
    background: #464646;
  }
`;

const ListLabel = styled.div`
  color: ${props => props.theme.primary};
`;

const Button = styled(Link)`
  background: ${props => props.theme.primary};
  text-decoration: none;
  display: block;
  margin: 1em 2em 0;
  height: 3em;
  line-height: 3em;
  border-radius: 1.5em;
  text-align: center;
`;

const Logo = styled.div`
  width: 3em;
  max-height: 100%;
  margin-right: 0.5em;
  img {
    width: 100%;
  }
`;

const ItemButton = styled.button`
  outline: none;
  background: inherit;
  border: none;
  padding: 0.5em;
`;

const ItemLogo = styled.img`
  max-width: 1.8em;
  max-height: 1.5em;
`;

/**
 * Displays booking on the history page
 */
export class BookingComponent extends PureComponent {
  state = {
    modalInfo: null,
    showRanking: false,
    percentageComplete: 0,
    gameAverage: 0,
    userRank: 0,
    hints: 0,
    wrongAnswers: 0,
    loading: true
  };
  constructor(props) {
    super(props);
    this.props.addTranslation(locale);
  }

  showItemModal = modalInfo => () => {
    this.setState({
      modalInfo
    });
  };

  closeItemModal = () => {
    this.setState({
      modalInfo: null
    });
  };

  handleClick = () => {
    this.props.onClick(this.props.bookingId);
    // Get ranking if booking opened for the first time
    if (this.state.loading) {
      this.getRanking();
    }
  };

  getRanking() {
    firebase
      .app()
      .functions('asia-northeast1')
      .httpsCallable('searchForBookingRanking')({
        gameId: this.props.gameId,
        bookingId: this.props.bookingId
      })
      .then(({ data }) => {
        // If backend returns showRanking as true, get and show all the ranking metrics
        // else hide this part on page
        if (data.showRanking) {
          this.setState({
            showRanking: data.showRanking,
            percentageComplete: data.percentageComplete.toFixed(1),
            gameAverage: (data.gameAverage * 100).toFixed(1),
            userRank: (data.userRank * 100).toFixed(1),
            hints: data.hints,
            wrongAnswers: data.wrongAnswers
          });
        }
        this.setState({ loading: false });
      });
  }

  getProgression() {
    if (this.props.gameState) {
      return this.props.gameState.progression;
    }
    // TODO SHC1 legacy data structure, to remove
    return this.props.stage;
  }

  /**
   * Show any items collected in the game
   */
  renderCollectedItems() {
    const state = this.getProgression();
    if (state == null) {
      return;
    }
    const collectedItems = Object.values(
      this.props.game.collectableItems || {}
    ).filter(collectableItem => collectableItem.progressionCriteria[state]);
    if (!collectedItems.length) {
      return;
    }
    return (
      <ListItem style={{ paddingRight: '1.5em' }}>
        <ListLabel>
          <Translate id="booking.items" />:
        </ListLabel>
        <span>
          {collectedItems.map(item => {
            return (
              <ItemButton key={item.name}>
                <ItemLogo
                  alt={item.name}
                  onClick={this.showItemModal(item)}
                  src={item.image}
                />
              </ItemButton>
            );
          })}
        </span>
        {this.state.modalInfo && (
          <Modal onClose={this.closeItemModal} title="">
            <ModalContainer>
              <Item>
                <ModalIcon
                  alt={this.state.modalInfo.name}
                  className="shimmer"
                  src={this.state.modalInfo.image}
                />
                <Caption>{this.state.modalInfo.name}</Caption>
              </Item>
              <ModalText>{this.state.modalInfo.description}</ModalText>
            </ModalContainer>
          </Modal>
        )}
      </ListItem>
    );
  }

  render() {
    const { open, locale, timeslot, photos, gameId } = this.props;
    const showAnswers = [6, 7, 8].includes(Number(gameId))
      ? this.getProgression() === 58
      : this.props.complete;
    const status =
      this.state.percentageComplete > MINIMUM_PERCENTAGE_COMPLETE_TO_SUCCESS
        ? 'success'
        : 'fail';
    return (
      <BookingContainer>
        <Ribbon onClick={this.handleClick} open={open}>
          <Logo>
            <img alt="" src={logos[gameId]} />
          </Logo>
          <Header>
            <Title title={this.props.game.name}>{this.props.game.name}</Title>
            <Subtitle>
              {dayjs
                .unix(timeslot.time)
                .locale(locale)
                .format('YYYY/MM/DD (ddd) HH:mm')}
            </Subtitle>
          </Header>
          <BookingButton type="button">
            {open ? <FaCaretUp /> : <FaCaretDown />}
          </BookingButton>
        </Ribbon>
        {open && (
          <BookingContent>
            {this.state.loading ? (
              <Indicator spin />
            ) : (
              this.state.showRanking && (
                <BookingMetrics>
                  <Status>
                    <ScoreLabel>
                      <Translate id="booking.scoreLabel" />:
                    </ScoreLabel>
                    <ScorePercentage>
                      {this.state.percentageComplete}%
                    </ScorePercentage>
                    <ScoreStatus>
                      <Translate id={`booking.scoreStatus.${status}`} />
                    </ScoreStatus>
                  </Status>
                  <div>
                    <ListItem>
                      <ListLabel>
                        <Translate id="booking.average" />:
                      </ListLabel>
                      <span>{this.state.gameAverage}%</span>
                    </ListItem>
                    <ListItem>
                      <ListLabel>
                        <Translate id="booking.rankLabel" />:
                      </ListLabel>
                      <span>
                        <Translate
                          data={{ rank: this.state.userRank }}
                          id="booking.rank"
                        />
                      </span>
                    </ListItem>
                    <ListItem>
                      <ListLabel>
                        <Translate id="booking.hints" />:
                      </ListLabel>
                      <span>
                        <Translate
                          data={{ count: this.state.hints }}
                          id="booking.counter"
                        />
                      </span>
                    </ListItem>
                    <ListItem>
                      <ListLabel>
                        <Translate id="booking.wrongAnswers" />:
                      </ListLabel>
                      <span>
                        <Translate
                          data={{ count: this.state.wrongAnswers }}
                          id="booking.counter"
                        />
                      </span>
                    </ListItem>
                    {this.renderCollectedItems()}
                  </div>
                </BookingMetrics>
              )
            )}
            {showAnswers && (
              <Button to={`/games/${gameId}/answers/`}>
                <Translate id="booking.answers" />
              </Button>
            )}
            {photos && <Gallery canDownload images={Object.values(photos)} />}
          </BookingContent>
        )}
      </BookingContainer>
    );
  }
}

export default compose(withLocalize)(BookingComponent);
